import AccessUserModal from '@/components/admin/AccessUserModal';
import StyledContainer from '@/components/styled/StyledContainer';
import StyledListHeader from '@/components/styled/StyledListHeader';
import StyledNextPreviousButtons from '@/components/styled/StyledNextPreviousButtons';
import StyledSelect from '@/components/styled/StyledSelect';
import StyledTable from '@/components/styled/StyledTable';
import Loader from '@/components/ui/Loader';
import { PAGE_SIZE } from '@/constants/pageSize';
import { useGetAllReceiversWithStats } from '@/hooks/useAdmin';
import useTableControls from '@/hooks/useTableControls';
import { ALL_RECEIVERS_COLUMNS } from '@/types/admin/allReceiversColumnDef';
import { UserType } from '@/types/auth/UserType';
import { LabelValue } from '@/types/global';
import { eachMonthOfInterval, format } from 'date-fns';
import { useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from '@/i18n';
import { useLanguage } from '@/contexts/LanguageContext';
import { LOCALES_MAP } from '@/constants/localeMap';

type filtersType = {
  month?: LabelValue<string>;
};

const AdminReceiversPage = () => {
  const { currentLanguage } = useLanguage();
  const { t } = useTranslation();

  const { control } = useForm<filtersType>({
    mode: 'onChange',
    defaultValues: { month: undefined },
  });
  const selectedMonth = useWatch({ control, name: 'month' });

  const filters = useMemo(
    () => ({
      month: selectedMonth?.value ?? undefined,
    }),
    [selectedMonth?.value],
  );

  const { page, setPage, sorting, setSorting, sortField, sortOrder } =
    useTableControls();

  const {
    data: allReceiversResponse,
    isSuccess,
    isLoading,
  } = useGetAllReceiversWithStats({
    page,
    pageSize: PAGE_SIZE,
    sortField,
    sortOrder,
    filters,
  });

  const monthOptions = useMemo(
    () =>
      eachMonthOfInterval({ start: new Date(2025, 0, 1), end: new Date() }).map(
        (month) => ({
          label: format(month, 'LLLL yyyy', {
            locale: LOCALES_MAP[currentLanguage],
          }),
          value: format(month, 'yyyy-MM'),
        }),
      ),
    [currentLanguage],
  );

  const [receiverToAccessId, setReceiverToAccessId] = useState<string | null>(
    null,
  );

  if (isLoading) return <Loader />;

  return (
    isSuccess && (
      <StyledContainer>
        <StyledListHeader
          title={t('pages.admin.receivers.title')}
          subTitle={t('pages.admin.receivers.subTitle')}
        />
        <div className="flex flex-col w-full h-auto p-6 gap-6">
          <div className="flex flex-col gap-2">
            <span className="text-xs font-normal text-gray-600">
              {t('pages.admin.receivers.filterBy')}
            </span>
            <div className="flex flex-col md:flex-row justify-between gap-4">
              <div className="flex flex-col md:flex-row gap-4">
                <StyledSelect
                  className="min-w-1/3 md:min-w-48 lg:min-w-60 xl:min-w-80"
                  control={control}
                  options={monthOptions}
                  name="month"
                  placeholder={t('pages.admin.receivers.month')}
                  isClearable={true}
                />
              </div>
            </div>
          </div>
          <StyledTable
            columns={ALL_RECEIVERS_COLUMNS}
            data={allReceiversResponse.receivers}
            sorting={sorting}
            setSorting={setSorting}
            getRowId={(row) => row.id}
            onViewRow={(row) => {
              setReceiverToAccessId(row.original.id);
            }}
          />
          <StyledNextPreviousButtons
            page={page}
            pageSize={PAGE_SIZE}
            setPage={setPage}
            dataLength={allReceiversResponse.total}
            label={t('pages.admin.receivers.receiver')}
          />
        </div>
        {receiverToAccessId && (
          <AccessUserModal
            closeModal={() => {
              setReceiverToAccessId(null);
            }}
            companyId={receiverToAccessId}
            type={UserType.Receiver}
          />
        )}
      </StyledContainer>
    )
  );
};

export default AdminReceiversPage;
